// Phase RV-6b — Personal Conviction Score panel.
//
// Source: ROLLOVER Intelligence Enhancement v1.0 §11.3.2.
//
// Doctrine:
//   - Calls flex_personal_conviction_score(auth.uid()) RPC on mount.
//   - N17 absolute: below 20 settled FLEX slips ALWAYS shows progress
//     state, never a fake score. Threshold gating reads the RPC
//     response — never client-computed.
//   - N-F06 v1: per-tier <10 shows the locked "keep building" copy.
//   - Composes Eyebrow + Stat + Badge + Pill + HairlineRow only.

import type { CSSProperties } from "react";
import { useMemo } from "react";
import { Badge, Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

// N17 — exact-string copy (grep-enforced).
export const CONVICTION_BUILDING_HEADLINE = "Building your conviction score";
export const CONVICTION_BUILDING_HELPER =
  "Lock and settle 20+ FLEX slips to unlock your Personal Conviction Score";

// N-F06 v1 sharpening — exact-string per-tier copy (grep-enforced).
export const TIER_KEEP_BUILDING = "Not enough FLEX data yet — keep building.";

const TIER_ORDER = ["2x", "3x", "4x", "5x", "6x"] as const;
type TierKey = (typeof TIER_ORDER)[number];

const TIER_LABEL: Record<TierKey, string> = {
  "2x": "2×",
  "3x": "3×",
  "4x": "4×",
  "5x": "5×",
  "6x": "6×",
};

interface TierBreakdownReady {
  ready: true;
  count: number;
  score: number;
}

interface TierBreakdownPending {
  ready: false;
  count: number;
  threshold: number;
}

type TierBreakdown = TierBreakdownReady | TierBreakdownPending;

interface RpcResponseBelow {
  ok: true;
  ready: false;
  settled_count: number;
  threshold: number;
}

interface RpcResponseReady {
  ok: true;
  ready: true;
  settled_count: number;
  overall_score: number;
  per_tier: Partial<Record<TierKey, TierBreakdown>>;
}

interface RpcResponseError {
  ok: false;
  reason: string;
}

type RpcResponse = RpcResponseBelow | RpcResponseReady | RpcResponseError;

function parseRpc(raw: unknown): RpcResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.ok === false) {
    return {
      ok: false,
      reason: typeof r.reason === "string" ? r.reason : "unknown",
    };
  }
  if (r.ok !== true) return null;
  const settled =
    typeof r.settled_count === "number" ? r.settled_count : Number(r.settled_count ?? 0);
  if (r.ready === false) {
    return {
      ok: true,
      ready: false,
      settled_count: settled,
      threshold: typeof r.threshold === "number" ? r.threshold : 20,
    };
  }
  if (r.ready !== true) return null;
  const overall =
    typeof r.overall_score === "number"
      ? r.overall_score
      : Number(r.overall_score ?? 0);
  const perTierRaw = (r.per_tier ?? {}) as Record<string, unknown>;
  const per_tier: Partial<Record<TierKey, TierBreakdown>> = {};
  for (const t of TIER_ORDER) {
    const entry = perTierRaw[t];
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    if (e.ready === true) {
      per_tier[t] = {
        ready: true,
        count: typeof e.count === "number" ? e.count : Number(e.count ?? 0),
        score: typeof e.score === "number" ? e.score : Number(e.score ?? 0),
      };
    } else if (e.ready === false) {
      per_tier[t] = {
        ready: false,
        count: typeof e.count === "number" ? e.count : Number(e.count ?? 0),
        threshold:
          typeof e.threshold === "number" ? e.threshold : Number(e.threshold ?? 10),
      };
    }
  }
  return {
    ok: true,
    ready: true,
    settled_count: settled,
    overall_score: overall,
    per_tier,
  };
}

export interface PersonalConvictionScoreProps {
  isAuthed: boolean;
  style?: CSSProperties;
}

function ProgressBar({ value, max }: { value: number; max: number }): JSX.Element {
  const pct = Math.max(0, Math.min(100, (value / Math.max(1, max)) * 100));
  return (
    <div
      data-testid="conviction-progress"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      style={{
        width: "100%",
        height: 6,
        background: "var(--sa-hairline)",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: "var(--sa-accent-saffron)",
        }}
      />
    </div>
  );
}

export function PersonalConvictionScore({
  isAuthed,
  style,
}: PersonalConvictionScoreProps): JSX.Element {
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const q = useRpcQuery<unknown>("flex_personal_conviction_score", {
    args: { p_user_id: userId },
    enabled: Boolean(userId),
  });
  const response: RpcResponse | null = useMemo(
    () => (userId && q.data !== undefined ? parseRpc(q.data) : null),
    [userId, q.data],
  );
  const loading = q.isLoading;
  const error = q.error ? (q.error.message ?? "Failed to load conviction score") : null;

  if (!isAuthed) {
    return (
      <section
        data-testid="conviction-anon"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 16,
          border: "1px solid var(--sa-hairline)",
          borderRadius: 4,
          ...style,
        }}
      >
        <Eyebrow>PERSONAL CONVICTION SCORE</Eyebrow>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-muted)",
          }}
        >
          Sign in to view your Personal Conviction Score.
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="conviction-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        padding: 16,
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
        ...style,
      }}
    >
      <Eyebrow>PERSONAL CONVICTION SCORE</Eyebrow>

      {loading && response === null ? (
        <div
          data-testid="conviction-loading"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-muted)",
          }}
        >
          Loading…
        </div>
      ) : null}

      {error ? (
        <div
          data-testid="conviction-error"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-danger, #b00020)",
          }}
        >
          {error}
        </div>
      ) : null}

      {response && response.ok && response.ready === false ? (
        <ConvictionBuilding
          settledCount={response.settled_count}
          threshold={response.threshold}
        />
      ) : null}

      {response && response.ok && response.ready === true ? (
        <ConvictionReady response={response} />
      ) : null}
    </section>
  );
}

function ConvictionBuilding({
  settledCount,
  threshold,
}: {
  settledCount: number;
  threshold: number;
}): JSX.Element {
  return (
    <div
      data-testid="conviction-building"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <div
        style={{
          fontFamily: "var(--sa-font-stat)",
          fontSize: 18,
          letterSpacing: "0.04em",
          color: "var(--sa-fg-primary)",
        }}
      >
        {CONVICTION_BUILDING_HEADLINE}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <Pill borderColor="var(--sa-hairline)">
          {settledCount} / {threshold} SETTLED
        </Pill>
      </div>
      <ProgressBar value={settledCount} max={threshold} />
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-muted)",
          lineHeight: 1.4,
        }}
      >
        {CONVICTION_BUILDING_HELPER}
      </div>
    </div>
  );
}

function ConvictionReady({
  response,
}: {
  response: RpcResponseReady;
}): JSX.Element {
  const overallPct = Math.round(response.overall_score * 100);
  return (
    <div
      data-testid="conviction-ready"
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <Stat
        label="OVERALL CONVICTION"
        value={`${overallPct}%`}
        display
        accent
        sub={`Across ${response.settled_count} settled FLEX slips`}
      />
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-muted)",
          lineHeight: 1.4,
        }}
      >
        Your conviction outperforms the agent baseline on {overallPct}% of picks.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {TIER_ORDER.map((t) => {
          const entry = response.per_tier[t];
          return (
            <HairlineRow key={t}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
                data-testid={`conviction-tier-${t}`}
              >
                <Pill borderColor="var(--sa-accent-saffron)" color="var(--sa-accent-saffron)">
                  {TIER_LABEL[t]}
                </Pill>
                {entry?.ready === true ? (
                  <>
                    <div
                      style={{
                        fontFamily: "var(--sa-font-stat)",
                        fontSize: 16,
                        color: "var(--sa-fg-primary)",
                      }}
                    >
                      {Math.round(entry.score * 100)}%
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--sa-font-mono)",
                        fontSize: 10,
                        color: "var(--sa-fg-muted)",
                      }}
                    >
                      {entry.count} settled
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                      <Badge variant="success">READY</Badge>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontFamily: "var(--sa-font-mono)",
                      fontSize: 11,
                      color: "var(--sa-fg-muted)",
                    }}
                    data-testid={`conviction-tier-${t}-keep-building`}
                  >
                    {TIER_KEEP_BUILDING}
                  </div>
                )}
              </div>
            </HairlineRow>
          );
        })}
      </div>
    </div>
  );
}
